import React, { CSSProperties, FC, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Flex, Skeleton, Spin, Tag, Tooltip, Typography } from 'antd'
import { ArrowUpOutlined, InfoCircleOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { EmptyState, PushlyAi, Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { CONTENT_DIRECTIVE_FIELDS } from '../content-directive-fields'
import { getClassNames } from '../../../_utils/classnames'
import {
    BRAND_VOICE_DISPLAY_TIER_VISUAL,
    BRAND_VOICE_TIER_VISUAL,
    DISPLAY_TIER_ORDER,
    type BrandVoiceDisplayTier,
} from './brand-voice-tier-visual'
import { collapsedGuaranteedSpec, guaranteedDisplayRows, guidanceSpanKey } from './text-highlight'
import { dimensionByBindingId } from './promotion-suggestions'
import './brand-voice-decomposition.scss'

export type CompileState = 'idle' | 'compiling' | 'done' | 'error'

// Only the two words the rest of the UI uses — "guarantees" and "guidance". An earlier set named
// a "best-effort" tier and an "AI judge", neither of which appears anywhere the author can see,
// so the wait taught vocabulary the result then failed to use.
const COMPILING_PHRASES = [
    'Reading through your Brand Voice…',
    'Separating enforced rules from guidance…',
    'Checking for emojis, exclamations, and banned words…',
    'Matching each rule to your words…',
    'Almost there…',
]
const COMPILING_PHRASE_INTERVAL_MS = 2800
const COMPILING_PHRASE_FADE_MS = 250

/**
 * Cycles through `phrases`, fading out briefly before swapping to the next one, only while
 * `active`. Resets to the first phrase whenever `active` turns true, so every new compile
 * starts the sequence fresh rather than resuming mid-cycle from the last one.
 */
function useCyclingPhrase(
    phrases: string[],
    intervalMs: number,
    fadeMs: number,
    active: boolean,
): { phrase: string; visible: boolean } {
    const [index, setIndex] = useState(0)
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        if (!active) return

        setIndex(0)
        setVisible(true)

        let fadeTimeoutId: ReturnType<typeof setTimeout>
        const intervalId = setInterval(() => {
            setVisible(false)
            fadeTimeoutId = setTimeout(() => {
                setIndex((i) => (i + 1) % phrases.length)
                setVisible(true)
            }, fadeMs)
        }, intervalMs)
        return () => {
            clearInterval(intervalId)
            clearTimeout(fadeTimeoutId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `phrases` is a module-level constant
    }, [active, intervalMs, fadeMs])

    return { phrase: phrases[index], visible }
}

/**
 * The platform's "an AI call is running" indicator, plus the rotating phrase.
 *
 * `PushlyAi animate` is the established treatment — `use-notification-content-loading-view` uses
 * exactly this for notification generation — so an author who has watched the notification form
 * think already recognises it here. Reused rather than reinvented precisely so it can become the
 * one signal that means "the AI is working", instead of a spinner that means anything.
 */
export const AiCompilingIndicator: FC<{ active?: boolean; size?: number }> = ({ active = true, size = 40 }) => {
    const { phrase, visible } = useCyclingPhrase(
        COMPILING_PHRASES,
        COMPILING_PHRASE_INTERVAL_MS,
        COMPILING_PHRASE_FADE_MS,
        active,
    )

    return (
        <Flex vertical align="center" gap={8} className={getClassNames('bvd-ai-indicator')}>
            <PushlyAi animate className={getClassNames('bvd-ai-indicator-mark')} style={{ fontSize: size }} />
            <Typography.Text
                type="secondary"
                className={getClassNames('bvd-compiling-phrase')}
                style={{
                    opacity: visible ? 1 : 0,
                    transition: `opacity ${COMPILING_PHRASE_FADE_MS}ms ease-in-out`,
                }}
            >
                {phrase}
            </Typography.Text>
        </Flex>
    )
}

interface IBrandVoiceDecomposition {
    state: CompileState
    compiled: CompiledDirective | null
    onRetry: () => void
    /** There is text to generate rules from, so the empty state points at Generate rules rather than at writing. */
    hasText?: boolean
    /**
     * A compile is running, but `compiled` already holds something worth looking at — keep it on
     * screen with a quiet indicator instead of replacing it with the full loading state.
     *
     * Exists for the promotion flow: confirming a promotion updates the decomposition locally and
     * kicks off the real compile behind it. Blanking to a spinner at that moment would hide the
     * author's own change for ten seconds, which is the entire thing the local update avoids.
     */
    refreshing?: boolean
    /**
     * Promotion suggestions grouped by the statement they are about, keyed on that
     * statement's exact text. Keyed by text rather than by tier+index because the tier a
     * statement lands in is the compiler's call and can move between compiles, while the
     * author's own sentence is what both sides actually agree on.
     *
     * Rendered on Checked, Guidance and Needs-review rows alike. Guidance carries almost all of
     * them in practice — measurement found vague length rules compiling to `style`, not
     * `assertions`, which makes them MORE worth promoting: guidance is never verified at all.
     */
    suggestionsByItem?: Map<string, DirectivePromotionSuggestion[]>
    /**
     * Opens the confirm-and-adjust step for one statement's suggestions. Receives the whole
     * group, since an ambiguous statement's alternatives are choices WITHIN that step rather
     * than separate things to promote.
     *
     * The row deliberately does not apply anything itself. The number becomes a hard limit on
     * the author's content, and offering it as a single click with no way to change the field,
     * the unit or the value made the most consequential part of the feature something to accept
     * or ignore. Omit to render suggestions read-only.
     */
    onPromote?: (suggestions: DirectivePromotionSuggestion[]) => void
}

/** True when the compiled spec is a non-empty PQuery condition group. */
function specHasConditions(spec: Record<string, unknown> | undefined): boolean {
    if (!spec) return false
    const group = (spec.and ?? spec.or) as unknown[] | undefined
    return Array.isArray(group) && group.length > 0
}

interface ISection {
    icon: React.ReactNode
    title: string
    caption: string
    count: number
    color: string
    /**
     * Full colored border + tinted background — reserved for a section that's genuinely an
     * exception worth flagging (Needs review). Guaranteed/Checked/Guidance are just three
     * flavors of "normal, expected content" competing for no one's urgent attention, so they
     * stay in the same neutral card chrome the rest of the product uses (see the Vespa
     * recommendation config's "Configuration Summary" card) — color there lives only on the
     * icon, not the whole box. Uniformly coloring all four would spend color-as-a-signal on
     * three sections that don't need it, diluting the one that does.
     */
    emphasized?: boolean
    children: React.ReactNode
}

// Uses the app's own `Well` (padding="small") rather than a hand-rolled bordered `div`, so this
// card tracks the platform's real theme tokens (including dark mode) instead of a set of
// manually-picked CSS var fallbacks that can silently drift from them.
export const InfoRow: FC<{
    children: React.ReactNode
    icon?: React.ReactNode
    style?: CSSProperties
    className?: string
}> = ({ children, icon, style, className }) => (
    <Well className={className} style={style} padding="small" variant="outline" hideHeader>
        <Flex align="flex-start" gap={8}>
            {icon && <span className={getClassNames('bvd-info-row-icon')}>{icon}</span>}
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </Flex>
    </Well>
)

// A tier is a heading over its rules, not a card: the editor's columns are already the frame, and a card per tier
// inside them nested a box in a box. The count sits beside the title, the way the settings card's switch shows it,
// and the explanation lives in the info icon's tooltip.
const Section: FC<ISection> = ({ icon, title, caption, count, color, emphasized, children }) => (
    <section className={getClassNames('bvd-section', { emphasized })} aria-label={title}>
        <Flex align="center" gap={8} className={getClassNames('bvd-section-header')}>
            <span style={{ color, display: 'inline-flex' }}>{icon}</span>
            <Typography.Text strong>
                {title} <span className={getClassNames('bvd-section-count')}>{count}</span>
            </Typography.Text>
            <Tooltip title={caption}>
                <InfoCircleOutlined className={getClassNames('bvd-section-info')} />
            </Tooltip>
        </Flex>
        <div className={getClassNames('bvd-section-body')}>{children}</div>
    </section>
)

/**
 * The subdued "this could be a guarantee" affordance under a prose-tier rule.
 *
 * An author who writes "Keep titles short" gets guidance applied best-effort and never verified.
 * The same intent with a number in it compiles to a deterministic binding. Nothing in the
 * product told them that, and this is the one place the gap is visible — the rule and the tier
 * it landed in are already on screen together.
 *
 * Styled as quiet supporting text inside the row it belongs to, NOT as a callout: this is
 * optional advice about text that is already working, and the decomposition has been kept
 * deliberately calm. It borrows the Guaranteed tier's color for the icon alone, so the affordance
 * says where the rule would MOVE TO without competing with the Needs-review section, which is the
 * one thing in this view that has earned an alarm color.
 */
const PromotionSuggestion: FC<{
    suggestions: DirectivePromotionSuggestion[]
    onPromote?: (suggestions: DirectivePromotionSuggestion[]) => void
}> = ({ suggestions, onPromote }) => {
    if (!suggestions.length || !onPromote) return null

    const lead = suggestions[0]
    // More than one entry means the statement named a length but no field, so these are
    // ALTERNATIVES for one sentence — see `groupSuggestionsByItem`. The row states that a choice
    // exists; the choice itself is made in the confirm step, where it sits next to the number it
    // changes instead of being frozen into a button label.
    const isChoice = suggestions.length > 1
    const dimension = dimensionByBindingId(lead.binding_id)

    return (
        <div className={getClassNames('bvd-promotion')}>
            <Flex align="flex-start" gap={6}>
                <span
                    className={getClassNames('bvd-promotion-icon')}
                    style={{ color: BRAND_VOICE_TIER_VISUAL.guaranteed.color }}
                >
                    <ArrowUpOutlined />
                </span>
                <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Text type="secondary" className={getClassNames('bvd-promotion-text')}>
                        {isChoice ? (
                            <>
                                This could be enforced as a{' '}
                                {/* The UNIT, but not the number or the field. Both of those depend
                                    on a choice the author has not made yet, and the row was
                                    showing the lead suggestion's — "a limit on 99 characters"
                                    over a rationale that said "your titles", which answers the
                                    very question the next click is about to ask. */}
                                <Typography.Text className={getClassNames('bvd-promotion-text')}>
                                    {dimension?.unit === 'words' ? 'word' : 'character'} limit
                                </Typography.Text>
                                .
                            </>
                        ) : (
                            <>
                                This could be enforced:{' '}
                                <Typography.Text className={getClassNames('bvd-promotion-text')}>
                                    “{lead.suggested_text}”
                                </Typography.Text>
                            </>
                        )}
                    </Typography.Text>
                    {/* The number is the author's to judge, not to trust — it becomes a hard
                        limit on their own content, so where it came from is shown inline rather
                        than hidden behind a tooltip. Omitted for a choice: every figure in it is
                        specific to one field, and there is no field yet. */}
                    {!isChoice && (
                        <Typography.Text type="secondary" className={getClassNames('bvd-promotion-rationale')}>
                            {lead.rationale}
                        </Typography.Text>
                    )}
                    <div>
                        <Button size="small" type="link" onClick={() => onPromote(suggestions)}>
                            {isChoice ? 'Choose a limit…' : 'Review and confirm…'}
                        </Button>
                    </div>
                </Flex>
            </Flex>
        </div>
    )
}

/**
 * Read-only decomposition of a compiled Brand Voice into the two tiers an author sees:
 *   - Guaranteed -> `spec`                            (deterministic PQuery, via PQueryBuilder)
 *   - Guidance   -> `assertions` + `style` + `unmapped` (told to the model, not verified)
 *
 * The compiler still emits four; the collapse is a DISPLAY decision, because "Checked" versus
 * "Needs review" carries no action an author can take. See `brand-voice-tier-visual.tsx`.
 *
 * Per-rule editing, promotion and conflict resolution are intentionally out of scope: this
 * view shows the author what their words compiled into, and nothing more.
 */
export const BrandVoiceDecomposition: FC<IBrandVoiceDecomposition> = ({
    state,
    compiled,
    onRetry,
    hasText = false,
    refreshing = false,
    suggestionsByItem,
    onPromote,
}) => {
    if (state === 'idle') {
        return (
            <EmptyState
                title="No rules yet"
                description={
                    hasText
                        ? 'Generate rules to see which rules your text creates.'
                        : 'Describe your Brand Voice to see which rules it creates.'
                }
            />
        )
    }

    // A background refresh over existing content never takes the panel away — see `refreshing`.
    if (state === 'compiling' && !(refreshing && compiled)) {
        return (
            <Flex vertical gap={12} className={getClassNames('bvd-status')}>
                <AiCompilingIndicator />

                {/* Only reachable on a FIRST compile — re-analyzing keeps the existing
                    decomposition under an overlay instead. So this is someone who has never seen
                    the result, staring at an empty panel for ten seconds. Showing the shape of
                    what is coming answers the question the wait creates: what is this panel even
                    going to say. */}
                {DISPLAY_TIER_ORDER.map((t) => BRAND_VOICE_DISPLAY_TIER_VISUAL[t]).map((tier, i) => (
                    <Well key={i} padding="small" shadow={true} hideHeader>
                        <Flex align="center" gap={8}>
                            <span style={{ color: tier.color, display: 'inline-flex' }}>{tier.icon}</span>
                            <Skeleton
                                active
                                title={{ width: 120 }}
                                paragraph={{ rows: 2, width: ['100%', '60%'] }}
                                className={getClassNames('bvd-skeleton')}
                            />
                        </Flex>
                    </Well>
                ))}
            </Flex>
        )
    }

    if (state === 'error' || !compiled) {
        return (
            <Alert
                type="error"
                showIcon
                title="Couldn’t check your Brand Voice"
                description="Something went wrong on our end. Your text is safe and nothing was changed. In the meantime, the AI keeps using your last saved rules."
                action={
                    <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
                        Retry
                    </Button>
                }
            />
        )
    }

    // The rows to SHOW, which is not one per PQuery condition — `banned_terms` fans out to one
    // condition per term. See `guaranteedDisplayRows`.
    //
    // Memoized because `guaranteedSpec` is handed to PQueryBuilder, whose provider re-initializes
    // on the prop's IDENTITY: a fresh object re-parses the whole document and resets the store.
    const guaranteedRows = useMemo(() => guaranteedDisplayRows(compiled), [compiled])
    const guaranteedSpec = useMemo(() => collapsedGuaranteedSpec(compiled), [compiled])

    const hasGuaranteed = specHasConditions(guaranteedSpec)

    // Everything the author sees as Guidance, in one list. Order is mandates -> persona ->
    // conditional, matching sailfish's `toDirectiveTierView` so the drawer and the API agree:
    // the firmest statements read first, which matters most where the list is long.
    const guidanceItems = useMemo(
        () => [
            ...(compiled.assertions ?? []).map((item, i) => ({ key: `checked-${i}`, text: item.text })),
            ...(compiled.style ?? []).map((item: any, i: number) => ({ key: guidanceSpanKey(i), text: item.text })),
            ...(compiled.unmapped ?? []).map((item, i) => ({ key: `needsReview-${i}`, text: item.text })),
        ],
        [compiled],
    )

    return (
        <Flex vertical gap={24} className={getClassNames('brand-voice-decomposition')}>
            {refreshing && (
                // Deliberately understated. The content below is a local prediction of what the
                // compiler will say, and it is very nearly always right — presenting that as a
                // warning would make the common case feel like a problem. It states what is
                // happening and nothing more.
                <Flex align="center" gap={8} className={getClassNames('bvd-refreshing')}>
                    <Spin size="small" indicator={<LoadingOutlined spin />} />
                    <Typography.Text type="secondary">Re-checking your Brand Voice…</Typography.Text>
                </Flex>
            )}
            <div data-bvd-tier="guaranteed">
                <Section
                    icon={BRAND_VOICE_TIER_VISUAL.guaranteed.icon}
                    title="Enforced"
                    caption="Checked automatically and enforced every time."
                    // Rows, not conditions. Counting conditions reported nine for what the author
                    // wrote as three sentences, because a term list fans out — a number they had no
                    // way to reconcile with their own text.
                    count={guaranteedRows.length}
                    color={BRAND_VOICE_TIER_VISUAL.guaranteed.color}
                >
                    {hasGuaranteed ? (
                        <PQueryBuilder
                            value={guaranteedSpec}
                            fields={CONTENT_DIRECTIVE_FIELDS}
                            disabled
                            // Overrides two tokens aqe re-declares on the builder's own
                            // root — see brand-voice-decomposition.scss's
                            // `.bvd-guaranteed-pqb` for why this has to be a class on the
                            // builder itself (via its own documented `className` prop)
                            // rather than a CSS variable set on a wrapping div.
                            // `bvw-rules-pqb` drops the builder's own "All of the following are true" header,
                            // as on the settings card: the Guaranteed heading already says what these are.
                            className={`${getClassNames('bvd-guaranteed-pqb')} ${getClassNames('bvw-rules-pqb')}`}
                            // Required unconditionally outside a PQueryBuilderProvider —
                            // even in `disabled` mode, the library throws without it. A
                            // no-op is correct here, not a gap to fill in: this view is
                            // read-only by design, and per-rule editing is out of scope
                            // (see the docstring above).
                            onRuleCommit={() => {}}
                            // Blank the per-row "and" connector, which repeats what the Guaranteed heading
                            // says. `disabled` above stops the interactions; these blank the "Add Rule" and
                            // "Add Group" labels so a read-only display shows no controls it does not offer.
                            labels={{
                                connectors: { and: '', or: '' },
                                addCondition: '',
                                addGroup: '',
                            }}
                        />
                    ) : (
                        <EmptyState
                            size="small"
                            align="start"
                            title="Nothing enforced yet"
                            description={
                                <>
                                    Phrases like{' '}
                                    <Typography.Text code className={getClassNames('bvd-empty-example')}>
                                        never…
                                    </Typography.Text>
                                    ,{' '}
                                    <Typography.Text code className={getClassNames('bvd-empty-example')}>
                                        always…
                                    </Typography.Text>
                                    , or{' '}
                                    <Typography.Text code className={getClassNames('bvd-empty-example')}>
                                        under 60 characters
                                    </Typography.Text>{' '}
                                    become enforced rules.
                                </>
                            }
                        />
                    )}
                </Section>
            </div>

            {/* ONE Guidance section, not three. `assertions` (judge-checked), `style` (persona)
                and `unmapped` (conditional/unplaceable) are all "told to the model, not
                guaranteed" from the author's side, and splitting them asked the author to act on
                a distinction that carries no action for them. */}
            <div data-bvd-tier="guidance">
                <Section
                    icon={BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance.icon}
                    title="Guidance"
                    // Parallel to Guaranteed's caption, on the one word that differs: both say
                    // whether the rule is checked automatically, so the pair reads as a single
                    // distinction rather than two unrelated descriptions.
                    caption="Always applied in your own words, but not checked automatically."
                    count={guidanceItems.length}
                    color={BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance.color}
                >
                    {guidanceItems.length > 0 ? (
                        // A light outline per line, not a card with its own padding and shadow: fifteen cards in a
                        // column were heavier than the rules they held, and a plain divided list ran together.
                        <ul className={getClassNames('bvd-guidance-list')}>
                            {guidanceItems.map((item) => (
                                <li key={item.key} className={getClassNames('bvd-guidance-item')}>
                                    <Typography.Paragraph className={getClassNames('bvd-guidance-text')}>
                                        {item.text}
                                    </Typography.Paragraph>
                                    <PromotionSuggestion
                                        suggestions={suggestionsByItem?.get(item.text) ?? []}
                                        onPromote={onPromote}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState size="small" align="start" title="No guidance yet" />
                    )}
                </Section>
            </div>
        </Flex>
    )
}

// Re-exported from its new home so existing importers keep working. It lives beside the display
// collapse now, because the two have to be read together to make sense of either.
export type { BrandVoiceTierKey, BrandVoiceDisplayTier } from './brand-voice-tier-visual'

interface IBrandVoiceSummaryChips {
    compiled: CompiledDirective
    /**
     * When provided (alongside `onTierToggle`), the active tier's chip is emphasized. What a
     * click MEANS is the caller's to decide: the settings card filters its text to that tier,
     * while the editor drawer scrolls to that tier's section. Both are "show me this tier",
     * and neither belongs in a component whose job is to render the summary.
     *
     * Omit both to render plain, non-interactive chips.
     */
    activeTier?: BrandVoiceDisplayTier | null
    onTierToggle?: (tier: BrandVoiceDisplayTier) => void
}

/**
 * Compact, glanceable summary of a compiled directive — the counts a settings-page card
 * should lead with instead of the raw free-text body, which explains what was said but not
 * what actually got enforced. Reads persisted `compiled_rules` only; never triggers a compile.
 *
 * Doubles as a filter: clicking a chip asks the caller to show only
 * that tier's highlights in the paired text view; clicking the same chip again clears it.
 */
export const BrandVoiceSummaryChips: FC<IBrandVoiceSummaryChips> = ({ compiled, activeTier, onTierToggle }) => {
    // The same row count the Guaranteed section shows. `resolved.length` counted fanned-out
    // PQuery conditions, so the strip read "7 Guaranteed" directly above a section headed
    // "Guaranteed 3" — two numbers for one thing, on screen together.
    const guaranteedCount = useMemo(() => guaranteedDisplayRows(compiled), [compiled]).length
    // One count spanning all three prose tiers — the same set the Guidance section lists.
    const guidanceCount =
        (compiled.assertions?.length ?? 0) + (compiled.style?.length ?? 0) + (compiled.unmapped?.length ?? 0)

    const chips = [
        { key: 'guaranteed', ...BRAND_VOICE_DISPLAY_TIER_VISUAL.guaranteed, text: `${guaranteedCount} Enforced` },
        // Always rendered, even at zero. A directive with no guidance is worth seeing as a fact
        // rather than as a missing chip, and the pair is what teaches the two-tier model.
        { key: 'guidance', ...BRAND_VOICE_DISPLAY_TIER_VISUAL.guidance, text: `${guidanceCount} Guidance` },
    ].filter((chip): chip is NonNullable<typeof chip> & { key: BrandVoiceDisplayTier } => chip !== null)

    const filterable = !!onTierToggle

    return (
        <Flex gap={8} wrap="wrap">
            {chips.map((chip) => {
                const isActive = activeTier === chip.key
                return (
                    <Tag
                        key={chip.key}
                        bordered={isActive}
                        onClick={filterable ? () => onTierToggle!(chip.key) : undefined}
                        style={{
                            color: chip.color,
                            background: isActive
                                ? `color-mix(in srgb, ${chip.color}, transparent 88%)`
                                : // Black at 3.5% is a soft grey on a light page and invisible on
                                  // a dark one. The fill token is the same idea expressed in
                                  // whichever theme is actually rendering.
                                  'var(--ant-color-fill-quaternary)',
                            borderColor: isActive ? chip.color : undefined,
                            cursor: filterable ? 'pointer' : undefined,
                        }}
                    >
                        {/* antd's Tag root is `display: inline-block` with its own fixed
                            line-height — not flex — so this row sits inside Tag's line box as an
                            ordinary INLINE box, positioned by `vertical-align` (default:
                            `baseline`) rather than by anything `align="center"` below controls.
                            `middle` is what actually centers the whole icon+text unit against
                            Tag's line height; align-items/lineHeight only govern the icon and
                            text's position RELATIVE TO EACH OTHER inside this row, not the row's
                            own position inside the tag. */}
                        <Flex
                            align="center"
                            gap={4}
                            style={{ display: 'inline-flex', verticalAlign: 'middle', lineHeight: 1 }}
                        >
                            <span style={{ display: 'inline-flex', fontSize: 12 }}>{chip.icon}</span>
                            <span>{chip.text}</span>
                        </Flex>
                    </Tag>
                )
            })}
        </Flex>
    )
}
